<template>
  <div>
    <Head title="Verifikasi Permohonan Beasiswa" />
    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/verify-scholar-applications">
        Verifikasi Permohonan
        Beasiswa
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ application.nama_pemohon }}
    </h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">Informasi Permohonan Beasiswa ({{ application.jenis }})</h2>
        <div v-if="application.status < 4" class="flex">
          <Button
            v-if="canWrite('verify permohonan beasiswa') && isAllFileVerified && !isPembatalanAktif && !isPenolakanAktif"
            type="button" class="mr-2 btn-green" @click.prevent="acceptApplication"
          >
            <span><font-awesome-icon icon="fas fa-check" class="mr-2" />Verifikasi</span>
          </Button>
          <Button
            v-if="canWrite('verify permohonan beasiswa') && isAllFileVerified && isPembatalanAktif" type="button"
            class="mr-2 btn-warning" @click.prevent="cancelApplication"
          >
            <span><font-awesome-icon icon="fas fa-ban" class="mr-2" />Belum Memenuhi Syarat</span>
          </Button>
          <Button
            v-if="canWrite('verify permohonan beasiswa') && isAllFileVerified && isPenolakanAktif" type="button"
            class="mr-2 btn-red" @click.prevent="rejectApplication"
          >
            <span><font-awesome-icon icon="fas fa-ban" class="mr-2" />Tidak Memenuhi Syarat</span>
          </Button>
        </div>
      </div>
      <div class="flex w-full">
        <dl class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-3/12">Periode Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ application.periode }}</dd>
          <dt class="w-full lg:w-3/12">NIM Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nim }}</dd>
          <dt class="w-full lg:w-3/12">Nama Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_pemohon }}</dd>
          <dt class="w-full lg:w-3/12">Nama Universitas</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_universitas }}</dd>
          <dt class="w-full lg:w-3/12">Program Studi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_prodi }}</dd>
          <dt class="w-full lg:w-3/12">Berprestasi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.is_berprestasi ? 'Ya' : 'Tidak' }}</dd>
          <dt class="w-full lg:w-3/12">Pra-Sejahtera</dt>
          <dd class="w-full lg:w-9/12">: {{ application.is_prasejatera ? 'Ya' : 'Tidak' }}</dd>
          <dt class="w-full lg:w-3/12">Semester</dt>
          <dd class="w-full lg:w-9/12">: {{ application.semester }}</dd>
          <dt class="w-full lg:w-3/12">Nilai UKT</dt>
          <dd class="w-full lg:w-9/12">: Rp. {{ $filters.formatNumber(application.nilai_ukt) }}</dd>
        </dl>
        <div class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-4/12">Jenis Kelamin</dt>
          <dd class="w-full lg:w-8/12">: {{ application.jenis_kelamin }}</dd>
          <dt class="w-full lg:w-4/12">Agama</dt>
          <dd class="w-full lg:w-8/12">: {{ application.agama }}</dd>
          <dt class="w-full lg:w-4/12">Alamat</dt>
          <dd class="w-full lg:w-8/12">: {{ application.alamat }}</dd>
          <dt class="w-full lg:w-4/12">Kecamatan/Kelurahan</dt>
          <dd class="w-full lg:w-8/12">: Kecamatan {{ application.kecamatan }} Kelurahan {{ application.kelurahan }}</dd>
          <dt class="w-full lg:w-4/12">RT/RW</dt>
          <dd class="w-full lg:w-8/12">: RT {{ application.rt }} RW {{ application.rw }}</dd>
          <dt class="w-full lg:w-4/12">Telepon</dt>
          <dd class="w-full lg:w-8/12">: {{ application.telp }}</dd>
          <dt class="w-full lg:w-4/12">Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: {{ application.nama_ayah }} / {{ application.nama_ibu }}</dd>
          <dt class="w-full lg:w-4/12">Penghasilan Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: Rp. {{ $filters.formatNumber(application.penghasilan_orangtua) }}</dd>
          <dt class="w-full lg:w-4/12">Jumlah Tanggungan Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: {{ application.jumlah_tanggungan_orangtua }} Orang</dd>
        </div>
      </div>
    </div>
    <div class="w-full bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">File Persyaratan</h2>
      </div>
      <div class="p-6">
        <ul class="mt-2 ml-4 list-disc">
          <li
            v-for="file in application.files" :key="`${file.id}-list`"
            :class="{ 'form-warning': file.status == 1, 'form-valid': file.status == 4, 'form-error': file.status == 2 || file.status == 3 }"
          >
            {{ file.persyaratan }} <font-awesome-icon
              v-if="file.status == 4" icon="fas fa-check"
              class="text-green-700"
            />
            <span v-if="file.status == 1" class="ml-1 form-warning">(Belum Verifikasi)</span>
          </li>
        </ul>
      </div>
      <table v-if="!statusInput" class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis File</th>
            <th class="px-6 pt-6 pb-4 text-center">Status Verifikasi</th>
            <th v-if="canWrite('verify permohonan beasiswa')" class="px-6 pt-6 pb-4 border-b">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-4 border-t">
              {{ file.persyaratan }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="file.status == 1" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Belum Verifikasi</span>
                <span v-else-if="file.status == 2" class="px-4 py-2 text-sm bg-red-300 rounded-lg">Belum Memenuhi
                  Syarat</span>
                <span v-else-if="file.status == 3" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi
                  Syarat</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-6 leading-4">{{ file.catatan_verifikasi }}</p>
            </td>
            <td v-if="canWrite('verify permohonan beasiswa')" class="w-px p-4 border-t">
              <Button
                v-if="file.status == 1" class="rounded-l btn-green-sm" title="Setujui"
                @click.prevent="setujuiFile(file.id)"
              >
                <font-awesome-icon icon="fas fa-check" />
              </Button>
              <Button v-if="file.status == 1" class="btn-red-sm" title="Tolak" @click.prevent="tolakFile(file.id)">
                <font-awesome-icon icon="fas fa-xmark" />
              </Button>
              <Button
                v-if="file.status == 1" class="btn-warning-sm" title="Batalkan"
                @click.prevent="batalkanFile(file.id)"
              >
                <font-awesome-icon icon="fas fa-ban" />
              </Button>
              <Button
                type="button" title="Lihat File" :class="{ 'rounded-l': file.status !== 1 }"
                class="rounded-r btn-green-sm" @click.prevent="viewImage(file.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.files.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data file persayaratan yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: {
    Head,
    Link,
    ImageViewer,
  },
  layout: Layout,
  props: {
    application: Object,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        scholar_application_id: this.application.id,
        type: null,
        file: null,
      }),
      imageData: '',
      statusInput: false,
      imageSource: '',
      showImage: false,
      catatan_verifikasi: '',
    }
  },
  computed: {
    isPembatalanAktif() {
      var validity = false

      for (let index = 0; index < this.application.files.length; index++) {
        const file = this.application.files[index]

        if (file.status == 2) {
          validity = true
          break
        }
      }
      return validity
    },
    isPenolakanAktif() {
      var validity = false

      for (let index = 0; index < this.application.files.length; index++) {
        const file = this.application.files[index]
        if (file.status == 3) {
          validity = true
          break
        }
      }
      return validity
    },
    isAllFileVerified() {
      var validity = true

      for (let index = 0; index < this.application.files.length; index++) {
        const file = this.application.files[index]

        if (file.status == 1) {
          validity = false
          break
        }
      }
      return validity
    },
  },

  methods: {
    setujuiFile(id) {
      this.$swal.fire({
        title: 'Anda yakin setujui file ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/scholar-application-files/${id}/accept`, {}, {
            preserveScroll: true,
          })
        }
      })
    },
    async batalkanFile(id) {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin batalkan file ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Pembatalan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/scholar-application-files/${id}/cancel`, {
          'catatan_verifikasi': catatan,
        }, {
          preserveScroll: true,
        })
      }
    },
    async tolakFile(id) {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin tolak file ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Penolakan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/scholar-application-files/${id}/reject`, {
          'catatan_verifikasi': catatan,
        }, {
          preserveScroll: true,
        })
      }
    },
    acceptApplication() {
      this.$swal.fire({
        title: 'Anda yakin setujui permohonan beasiswa ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/scholar-applications/${this.application.id}/verify`)
        }
      })
    },
    async cancelApplication() {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin batalkan permohonan beasiswa ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Pembatalan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/scholar-applications/${this.application.id}/cancel`, {
          'catatan_verifikasi': catatan,
        })
      }
    },
    async rejectApplication() {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin tolak permohonan beasiswa ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Penolakan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/scholar-applications/${this.application.id}/reject`, {
          'catatan_verifikasi': catatan,
        })
      }
    },
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
  },
}
</script>
<style scoped>
.vuejs3-datepicker__value {
  width: 100%;
  border-color: rgba(226, 232, 240, var(--tw-border-opacity));
}
</style>
